import { groupBy, range, sortBy } from 'lodash';
import { PointsAndRows, RowOrderProps } from '../../types/pointsAndRows';

/**
 * NOTE: these are required for all section types since reversePointsAndRows is
 * called by generatePointsAndRows. This is not currently supported for imported
 * sections.
 */
export const SHARED_GENERATE_INPUTS = [
  'rowOrderIsReversed',
  'orderWithinRowsIsReversed',
];

/**
 * Takes in `pointsAndRows`, an object with `rows` and `points` keys, and
 * reverses the order of rows and/or the order of points within rows, depending
 * on the values of `rowOrderIsReversed` and `orderWithinRowsIsReversed`.
 *
 * Note: the `pointsAndRows` input will be mutated, don't rely on it being
 * unchanged.
 * Note: the `pointsAndRows` input may have other keys (e.g. rotationCenter),
 * and we want to preserve these.
 */
const reversePointsAndRows = (
  pointsAndRows: PointsAndRows,
  { rowOrderIsReversed, orderWithinRowsIsReversed }: RowOrderProps,
) => {
  // NOTE: this can be null - if so, return early:
  if (!pointsAndRows) return pointsAndRows;
  const { rows, points } = pointsAndRows;

  // In `rows`, flip row order if necessary
  if (rows?.features?.length && rowOrderIsReversed) {
    rows.features.reverse();

    // Update the values of `properties.row`
    range(rows.features.length).forEach((i) => {
      rows.features[i].properties = {
        ...(rows.features[i].properties || {}),
        row: i,
      };
    });
  }

  // In `rows`, flip in-row order if necessary
  if (rows?.features?.length && orderWithinRowsIsReversed) {
    rows.features.forEach((feature) => feature.geometry.coordinates.reverse());
  }

  // Rebuild `points` if `rows` was adjusted
  if (
    points?.features?.length &&
    (rowOrderIsReversed || orderWithinRowsIsReversed)
  ) {
    // Sorted pairs of [row, pointsInRow], (pointsInRow is an array of points)
    const pointsByRowEntries = sortBy(
      Object.entries(groupBy(points.features, 'properties.row')),
      ([row]) => row,
    );

    if (rows?.features?.length && rowOrderIsReversed) {
      pointsByRowEntries.reverse();

      const lastRowIndex = rows.features.length - 1;
      pointsByRowEntries.forEach(([originalRow, pointsInRow]) => {
        const row = lastRowIndex - parseInt(originalRow, 10);
        // Update the values of `properties.row`
        pointsInRow.forEach((feature) => {
          // eslint-disable-next-line no-param-reassign
          feature.properties = { ...(feature.properties || {}), row };
        });
      });
    }

    if (orderWithinRowsIsReversed) {
      pointsByRowEntries.forEach(([, pointsInRow]) => pointsInRow.reverse());
    }

    points.features = pointsByRowEntries
      .map(([, pointsInRow]) => pointsInRow)
      .flat();
  }

  return pointsAndRows;
};

export default reversePointsAndRows;
